import { CalendarDays } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";

/** "29/10/2026", "29-10-26" or "2026-10-29" → "2026-10-29"; anything else → null. */
export function parseDateText(raw: string): string | null {
  const text = raw.trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  const local = /^(\d{1,2})[/.\- ](\d{1,2})[/.\- ](\d{2}|\d{4})$/.exec(text);
  const [year, month, day] = iso
    ? [Number(iso[1]), Number(iso[2]), Number(iso[3])]
    : local
      ? [Number(local[3]!.length === 2 ? `20${local[3]}` : local[3]), Number(local[2]), Number(local[1])]
      : [0, 0, 0];
  if (!year) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}
const shown = (iso: string) => (/^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso.split("-").reverse().join("/") : "");

/**
 * Date typed as dd/mm/aaaa with a calendar button. Replaces the native date box, which Safari shows
 * pre-filled in grey with today's date while it is actually empty.
 * `name` posts the parsed date (aaaa-mm-dd) with the form, so what is written is what gets saved.
 */
export function DateBox({ label, name, value, min, className = "", onChange }: {
  label: string;
  name: string;
  value: string;
  min?: string;
  className?: string;
  onChange: (iso: string) => void;
}) {
  const id = useId();
  const picker = useRef<HTMLInputElement>(null);
  const [text, setText] = useState(shown(value));
  // Follow outside changes without undoing a date that is still being typed.
  useEffect(() => {
    setText((current) => (parseDateText(current) === value ? current : shown(value)));
  }, [value]);
  const typed = (raw: string) => {
    setText(raw);
    const iso = parseDateText(raw);
    if (iso || !raw.trim()) onChange(iso ?? "");
  };
  return (
    <label className={`field date-box ${className}`} htmlFor={id}>
      <span className="field__label">{label}</span>
      <span className="field__control">
        <input id={id} inputMode="numeric" autoComplete="off" placeholder="dd/mm/aaaa" value={text} onChange={(e) => typed(e.target.value)} />
        <input type="hidden" name={name} value={parseDateText(text) ?? ""} />
        <span className="date-box__calendar">
          <CalendarDays size={18} aria-hidden="true" />
          <input
            ref={picker}
            type="date"
            tabIndex={-1}
            aria-label={`Calendario · ${label}`}
            value={parseDateText(text) ?? ""}
            min={min}
            onClick={() => {
              try { picker.current?.showPicker?.(); } catch { /* the browser opens its own picker */ }
            }}
            onChange={(e) => { setText(shown(e.target.value)); onChange(e.target.value); }}
          />
        </span>
      </span>
    </label>
  );
}
